import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { Resource } from "@mapmycustomers/shared/types/entity";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Organization from "@mapmycustomers/shared/types/Organization";
import { ResourceResponse } from "@mapmycustomers/shared/types/resources/ResourceResponse";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import resourceFieldModel, { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import { callApi } from "../api/callApi";
import { handleError } from "../errors/actions";
import { getOrganizationId, getPinnedResourceIds } from "../iam";

import { fetchFolders, fetchPinnedResources, fetchResources } from "./actions";

const DEFAULT_LIMIT = 100;

export function* onFetchResources({ payload }: ReturnType<typeof fetchResources.request>) {
  const { failureCallback, successCallback, viewState } = payload;

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const { filter, range, sort } = viewState;
    const platformFilter = convertToPlatformFilterModel(
      filter ?? createEmptyFilterModel(),
      [],
      resourceFieldModel
    );

    const response: ResourceResponse = yield callApi("fetchResources", orgId, {
      $filters: platformFilter,
      $limit: range ? range.endRow - range.startRow : DEFAULT_LIMIT,
      $offset: range?.startRow ?? 0,
      $order: sort ? convertToPlatformSortModel(sort) : ResourceFieldName.NAME,
    });

    yield call(successCallback, { resources: response.data, total: response.total });
    yield put(fetchResources.success());
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(fetchResources.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchFolders({ payload }: ReturnType<typeof fetchFolders.request>) {
  const { callback, query } = payload;
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: ListResponse<Folder> = yield callApi("fetchFolders", orgId, {
      $filters: {
        $and: [query ? { name: { $in: query } } : {}],
      },
      $limit: DEFAULT_LIMIT,
      $order: "name",
    });

    yield call(callback, response.data);
    yield put(fetchFolders.success());
  } catch (error) {
    yield put(fetchFolders.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPinnedResources({
  payload,
}: ReturnType<typeof fetchPinnedResources.request>) {
  const { failureCallback, successCallback } = payload;

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const pinnedResourceIds: Resource["id"][] = yield select(getPinnedResourceIds);

    if (pinnedResourceIds.length === 0) {
      yield call(successCallback, []);
      yield put(fetchPinnedResources.success());
      return;
    }

    const response: ResourceResponse = yield callApi("fetchResources", orgId, {
      $filters: { $and: [{ id: { $in: pinnedResourceIds } }] },
      $limit: 1000,
    });

    const resourceMap = new Map(response.data.map((resource) => [resource.id, resource]));
    const pinnedResources = pinnedResourceIds
      .filter((id) => resourceMap.has(id))
      .map((id) => resourceMap.get(id)!);

    yield call(successCallback, pinnedResources);
    yield put(fetchPinnedResources.success());
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(fetchPinnedResources.failure());
    yield put(handleError({ error }));
  }
}

export function* resourcesSaga() {
  yield takeEvery(fetchResources.request, onFetchResources);
  yield takeEvery(fetchFolders.request, onFetchFolders);
  yield takeLatest(fetchPinnedResources.request, onFetchPinnedResources);
}
